import { z } from "zod";

export const toolCategorySchema = z.enum([
  "admin",
  "assetLevelDeepDive",
  "coordinateGeneration",
  "create",
  "dataVisualisation",
  "marketIntelligence",
  "reference",
  "structural",
  "screening",
  "comprehensive",
]);

export const toolPropsSchema = z.object({
  isTutorialDisabled: z.boolean(),
  isTutorialOpen: z.boolean(),
  setIsTutorialDisabled: z.function().args(z.boolean()),
  setIsTutorialOpen: z.function().args(z.boolean()),
});

export const toolBaseSchema = z.object({
  id: z.string(),
  category: toolCategorySchema,
  keyPrefix: z.string(),
  icon: z.custom<JSX.Element>(),
  render: z.function().args(toolPropsSchema).returns(z.custom<JSX.Element>()),

  maxWidth: z.string().optional(),
  hasTutorial: z.boolean().optional(),
  showUsage: z.boolean().optional(),
  /**
   * When true, the tool owns the entire viewport: no sidebar, no default top
   * bar, no ToolPage title/subtitle. The tool's render() output is the whole
   * page (still behind the auth/org-key/terms guards).
   */
  fullScreen: z.boolean().optional(),
  /**
   * For tools that don't render themselves, but instead link elsewhere.
   * Can be used for internal or external links. Accepts an optional Hub
   * access token (e.g. for Document360 SSO deep-links) and may return the
   * URL synchronously or as a Promise.
   */
  buildSidebarRedirect: z.custom<(accessToken?: string) => string | Promise<string>>().optional(),

  /**
   * Especially useful for database tools such as Embed Pages.
   */
  overrides: z
    .object({
      isVisible: z.boolean().optional(),
      title: z.string().optional(),
      subtitle: z.string().optional(),
      description: z.string().optional(),
      menuLabel: z.string().optional(),
    })
    .optional(),
  banner: z
    .object({
      enabled: z.function().returns(z.boolean()),
      icon: z.custom<JSX.Element>().optional(),
      tag: z.string().or(z.custom<JSX.Element>()).optional(),
      description: z.string().or(z.custom<JSX.Element>()),
    })
    .optional(),
});

export const toolSchema = toolBaseSchema.extend({
  /**
   * Tools needed for the function of the parent tool (primarily /create pages).
   */
  subTools: z.array(toolBaseSchema).optional(),
});

export const toolsObjectSchema = z.record(toolCategorySchema, z.array(toolSchema));

export const screeningToolTypeSchema = z.enum(["screening.single", "screening.batch"]);

export type Tool = z.infer<typeof toolSchema>;
export type ToolCategory = z.infer<typeof toolCategorySchema>;
export type ToolProps = z.infer<typeof toolPropsSchema>;
export type ToolsObject = Required<z.infer<typeof toolsObjectSchema>>;
export type ScreeningToolType = z.infer<typeof screeningToolTypeSchema>;
